/**
 * CorporateMonthlyInputs — /corporate/monthly/inputs
 * Monthly GHG data entry for corporate periodic workspace.
 * Scope 1/2/3 inputs, IFRS S2 intensity metrics, sector-specific schema.
 */
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { RefreshCw, Save, Copy, Lock, CheckCircle2, ChevronDown, Building2, FileDown } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";
import { useCorporatePeriodic } from "@/hooks/useCorporatePeriodic";

import { PageHeader, SectionCard, MetricCard, CTAButton } from "@/components/ds";
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1];

const SCOPE_COLORS = { 1: "text-amber-700", 2: "text-teal-700", 3: "text-blue-700" };
const GROUP_SCOPE = (g) => g.includes("Scope 1") ? 1 : g.includes("Scope 2") ? 2 : g.includes("Scope 3") ? 3 : null;

function PeriodSelector({ year, month, onYearChange, onMonthChange, periods }) {
  const hasData  = (m) => periods.some(p => p.period_label === `${year}-${String(m).padStart(2,"0")}`);
  const isLocked = (m) => periods.find(p => p.period_label === `${year}-${String(m).padStart(2,"0")}`)?.is_locked;

  return (
    <SectionCard title="Reporting Period" testid="period-selector">
      <div className="flex items-center gap-2 mb-3">
        <p className="text-xs text-slate-500 w-10">Year</p>
        <div className="flex gap-1.5">
          {YEARS.map(y => (
            <button key={y} onClick={() => onYearChange(y)}
              className={`px-3 py-1 text-xs font-bold rounded-lg ${y === year ? "bg-[#0F172A] text-white" : "border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {y}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {MONTHS.map((m, i) => {
          const mo = i + 1;
          const active = mo === month;
          const has    = hasData(mo);
          const locked = isLocked(mo);
          return (
            <button key={m} onClick={() => onMonthChange(mo)}
              className={`py-2 text-xs font-bold rounded-xl relative ${
                active ? "bg-[#0F172A] text-white" :
                locked ? "bg-emerald-50 text-emerald-700 border border-emerald-200" :
                has    ? "bg-teal-50 text-teal-700 border border-teal-200" :
                "border border-slate-100 text-slate-400 hover:border-slate-300"
              }`}
              data-testid={`month-${mo}`}>
              {m}
              {locked && <span className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400" />}
            </button>
          );
        })}
      </div>
      <div className="flex gap-3 mt-3 text-xs text-slate-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-teal-100 border border-teal-200 rounded" /> Calculated</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-100 border border-emerald-200 rounded" /> Locked</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 border border-slate-200 rounded" /> No data</span>
      </div>
    </SectionCard>
  );
}

function InputGroup({ title, fields, values, onChange, locked }) {
  const [open, setOpen] = useState(true);
  const scopeNum = GROUP_SCOPE(title);
  const scopeColor = scopeNum ? SCOPE_COLORS[scopeNum] : "text-slate-600";

  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden mb-3">
      <button className="w-full flex items-center justify-between px-5 py-4 border-b border-slate-100 hover:bg-slate-50 transition-colors"
        onClick={() => setOpen(v => !v)}>
        <div className="flex items-center gap-2">
          {scopeNum && (
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg border ${
              scopeNum === 1 ? "bg-amber-50 border-amber-200 text-amber-700" :
              scopeNum === 2 ? "bg-teal-50 border-teal-200 text-teal-700" :
              "bg-blue-50 border-blue-200 text-blue-700"
            }`}>S{scopeNum}</span>
          )}
          <p className={`text-sm font-semibold ${scopeColor}`}>{title}</p>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 py-4 grid grid-cols-2 gap-4">
          {fields.map(f => (
            <div key={f.key}>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                {f.label} <span className="text-slate-400 font-normal">({f.unit})</span>
                {f.priority && <span className="ml-1 text-teal-500 text-xs font-semibold">Priority</span>}
              </label>
              <input
                type="number"
                value={values[f.key] ?? ""}
                onChange={e => onChange(f.key, e.target.value)}
                disabled={locked}
                placeholder={f.default != null ? String(f.default) : "0"}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-400"
                data-testid={`input-${f.key}`}
              />
              {f.hint && <p className="text-xs text-slate-400 mt-0.5 italic truncate">{f.hint}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CorporateMonthlyInputs() {
  const navigate = useNavigate();
  const { selectedYear, setSelectedYear, selectedMonth, setSelectedMonth,
          periodLabel, currentPeriod, allPeriods, orgName, loading, refresh, refreshPeriod } = useCorporatePeriodic();

  const [form,    setForm]    = useState({});
  const [schema,  setSchema]  = useState(null);
  const [sector,  setSector]  = useState("general");
  const [target,  setTarget]  = useState("");
  const [saving,  setSaving]  = useState(false);
  const [locking, setLocking] = useState(false);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    client.get(`/corporate/periodic/config/${sector}`)
      .then(r => setSchema(r.data))
      .catch(() => {});
  }, [sector]);

  useEffect(() => {
    if (currentPeriod?.input_snapshot) {
      setForm(Object.fromEntries(
        Object.entries(currentPeriod.input_snapshot).map(([k, v]) => [k, String(v)])
      ));
    } else {
      setForm({});
    }
  }, [currentPeriod]);

  const isLocked = currentPeriod?.is_locked || false;
  const handleChange = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const handleCopyForward = async () => {
    const pm = selectedMonth === 1 ? 12 : selectedMonth - 1;
    const py = selectedMonth === 1 ? selectedYear - 1 : selectedYear;
    const fromPeriod = `${py}-${String(pm).padStart(2,"0")}`;
    setCopying(true);
    try {
      const res = await client.post("/corporate/periodic/copy-forward", {
        from_period: fromPeriod, to_period: periodLabel, sector,
      });
      setForm(Object.fromEntries(
        Object.entries(res.data.prefilled_inputs || {}).map(([k, v]) => [k, String(v)])
      ));
      toast.success(`Copied from ${fromPeriod} — review and update before saving.`);
    } catch {
      toast.error("No prior period found to copy from.");
    }
    setCopying(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const inputs = {};
    (schema?.input_schema || []).forEach(f => {
      const v = form[f.key];
      if (v !== "" && v != null) inputs[f.key] = parseFloat(v) || 0;
      else if (f.default != null) inputs[f.key] = f.default;
    });
    try {
      await client.post("/corporate/periodic/calculate", {
        period_year: selectedYear, period_month: selectedMonth,
        sector, inputs,
        annual_target_tco2e: target ? parseFloat(target) : null,
      });
      await refreshPeriod();
      toast.success(`${periodLabel} calculated successfully.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Calculation failed.");
    }
    setSaving(false);
  };

  const handleLock = async () => {
    if (!confirm(`Lock ${periodLabel}? Locked periods cannot be recalculated.`)) return;
    setLocking(true);
    try {
      await client.put(`/corporate/periodic/period/${periodLabel}/lock`);
      await refreshPeriod();
      toast.success(`${periodLabel} locked.`);
    } catch { toast.error("Lock failed."); }
    setLocking(false);
  };

  const handleDownloadReport = async (tier) => {
    try {
      const resp = await client.get(
        `/periodic-report/corporate/${periodLabel}?tier=${tier}`,
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a   = document.createElement("a");
      a.href     = url;
      a.download = `DCI-Corporate-${periodLabel}-${tier.toUpperCase()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${tier === "snapshot" ? "Snapshot" : "Institutional"} report downloaded.`);
    } catch {
      toast.error("Report generation failed — ensure period has been calculated and confidence score >= 40.");
    }
  };

  const groups = {};
  (schema?.input_schema || []).forEach(f => {
    if (!groups[f.group]) groups[f.group] = [];
    groups[f.group].push(f);
  });

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  return (
    <div className="p-6 lg:p-8 min-h-screen">
        <button onClick={() => navigate("/corporate")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 mb-5">
          ← Corporate Workspace
        </button>

        <div className="flex items-start justify-between mb-5">
          <div>
            
      <PageHeader
        label="Corporate Monthly Tracking"
        title="Monthly GHG Data Entry"
        backUrl="/corporate"
        backLabel="Corporate Workspace"
      />

      <MetricCard label="Input Fields" value={SCOPE_FIELDS.length} color="text-slate-900" />
            <p className="text-xs text-slate-400 mt-0.5">
              {orgName && <span className="text-slate-600 font-medium">{orgName} · </span>}
              Period: <strong>{periodLabel}</strong>
              {currentPeriod && <span className="ml-2 text-xs font-bold text-teal-600">v{currentPeriod.version_number} calculated</span>}
              {isLocked && <span className="ml-2 text-xs font-bold text-emerald-600">LOCKED</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleCopyForward} disabled={copying || isLocked}
              className="flex items-center gap-1.5 px-4 py-2 border border-slate-200 text-slate-600 text-xs font-bold rounded-xl hover:bg-slate-50 disabled:opacity-50"
              data-testid="copy-forward-btn">
              {copying ? <RefreshCw size={13} className="animate-spin" /> : <Copy size={13} />}
              Copy Prior Month
            </button>
            {currentPeriod && !isLocked && (
              <button onClick={handleLock} disabled={locking}
                className="flex items-center gap-1.5 px-4 py-2 border border-emerald-300 text-emerald-700 text-xs font-bold rounded-xl hover:bg-emerald-50"
                data-testid="lock-period-btn">
                <Lock size={13} /> Lock Period
              </button>
            )}
            {currentPeriod && (
              <div className="relative group">
                <button
                  className="flex items-center gap-1.5 px-4 py-2 border border-blue-200 text-blue-700 text-xs font-bold rounded-xl hover:bg-blue-50"
                  data-testid="download-report-btn">
                  <FileDown size={13} /> Report
                </button>
                <div className="absolute right-0 top-9 bg-white border border-slate-200 rounded-xl shadow-lg z-10 hidden group-hover:block min-w-[190px]">
                  <button onClick={() => handleDownloadReport("snapshot")}
                    className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 rounded-t-xl"
                    data-testid="download-snapshot-btn">
                    Snapshot Report (6-8pp)
                  </button>
                  <button onClick={() => handleDownloadReport("institutional")}
                    className="w-full text-left px-4 py-2.5 text-xs text-slate-700 hover:bg-slate-50 rounded-b-xl border-t border-slate-100"
                    data-testid="download-institutional-btn">
                    Institutional Report (12-18pp)
                  </button>
                </div>
              </div>
            )}
            <button onClick={handleSave} disabled={saving || isLocked}
              className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white text-xs font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50"
              data-testid="calculate-btn">
              {saving ? <><RefreshCw size={13} className="animate-spin" /> Calculating...</> : <><Save size={13} /> Calculate</>}
            </button>
          </div>
        </div>

        {/* Decision badge */}
        {currentPeriod?.decision?.outcome && (
          <div className={`rounded-xl px-4 py-2.5 mb-4 flex items-center justify-between border ${
            ["ready_for_management_review","ready_for_external_reporting","proceed_to_credit_committee"].includes(currentPeriod.decision.outcome)
              ? "bg-emerald-50 border-emerald-200"
              : currentPeriod.decision.outcome === "proceed_with_conditions"
              ? "bg-amber-50 border-amber-200"
              : "bg-orange-50 border-orange-200"
          }`} data-testid="decision-outcome-banner">
            <div>
              <p className="text-xs font-bold text-slate-700">Decision: {currentPeriod.decision.outcome_label}</p>
              <p className="text-xs text-slate-500 mt-0.5">{currentPeriod.decision.description}</p>
            </div>
            <span className="text-xs font-bold text-slate-500 ml-4">
              Score: {currentPeriod.decision.confidence_score}/100
            </span>
          </div>
        )}

        {isLocked && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-xl px-4 py-2.5 mb-4 flex items-center gap-2">
            <CheckCircle2 size={13} className="text-emerald-500" />
            <p className="text-xs text-emerald-700 font-semibold">
              Period {periodLabel} is locked — immutable.
              Total: {currentPeriod?.total_tco2e} tCO2e
              (S1: {currentPeriod?.scope1_tco2e} | S2: {currentPeriod?.scope2_tco2e} | S3: {currentPeriod?.scope3_tco2e})
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: Period selector + config */}
          <div className="space-y-4">
            <PeriodSelector year={selectedYear} month={selectedMonth}
              onYearChange={setSelectedYear} onMonthChange={setSelectedMonth}
              periods={allPeriods} />

            <SectionCard>

              <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">Configuration</p>
              <div className="mb-3">
                <label className="block text-xs font-semibold text-slate-600 mb-1">Sector</label>
                <select value={sector} onChange={e => setSector(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="sector-select">
                  {Object.entries(schema?.available_sectors || {}).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              {schema?.ifrs_s2_sector && (
                <p className="text-xs text-teal-600 mb-2">
                  IFRS S2: {schema.ifrs_s2_sector}
                </p>
              )}
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Annual Target (tCO2e)</label>
                <input type="number" value={target} onChange={e => setTarget(e.target.value)}
                  placeholder="Optional SBTi / NDC target"
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-xl"
                  data-testid="annual-target-input" />
              </div>
                      </SectionCard>

            {/* Results panel */}
            {currentPeriod && (
              <div className="bg-[#0F172A] rounded-xl p-5" data-testid="monthly-results">
                <p className="text-xs text-slate-400 uppercase tracking-wider font-semibold mb-3">{periodLabel} Results</p>
                {[
                  ["Total",   `${currentPeriod.total_tco2e}`,  "text-teal-400"],
                  ["Scope 1", `${currentPeriod.scope1_tco2e}`, "text-amber-400"],
                  ["Scope 2", `${currentPeriod.scope2_tco2e}`, "text-teal-300"],
                  ["Scope 3", `${currentPeriod.scope3_tco2e}`, "text-blue-400"],
                ].map(([l, v, c]) => (
                  <div key={l} className="bg-white/5 rounded-xl px-3 py-2 mb-1.5 flex justify-between">
                    <p className="text-xs text-slate-500">{l}</p>
                    <p className={`text-xs font-bold ${c}`}>{v} tCO2e</p>
                  </div>
                ))}
                {currentPeriod.intensity && Object.entries(currentPeriod.intensity).slice(0, 3).map(([k, v]) => (
                  <div key={k} className="bg-white/5 rounded-xl px-3 py-2 mb-1.5 flex justify-between">
                    <p className="text-xs text-slate-500">{k.replace(/_/g," ")}</p>
                    <p className="text-xs font-bold text-purple-400">{typeof v === "number" ? v.toFixed(4) : v}</p>
                  </div>
                ))}
                <p className="text-xs text-slate-600 mt-2">IFRS S2 intensity metrics — not third-party verified</p>
              </div>
            )}
          </div>

          {/* Right: Input groups */}
          <div className="lg:col-span-2">
            {Object.entries(groups).map(([groupName, fields]) => (
              <InputGroup key={groupName} title={groupName} fields={fields}
                values={form} onChange={handleChange} locked={isLocked} />
            ))}
            <p className="text-xs text-slate-400 italic mt-2">
              GHG Protocol Corporate Standard (Scope 1/2/3) · DCI-CORPORATE-PERIODIC-V1.0 ·
              Self-reported data — Platform-verified (not third-party VVB validated)
            </p>
          </div>
        </div>
    </div>
  );
}
